import { forwardRef, memo, useState } from "react";
import { AltArrowRight, Magnifier } from "@solar-icons/react/ssr";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import TableCell from "@mui/material/TableCell";
import TextField from "@mui/material/TextField";
import { NumericFormat } from "react-number-format";
import styled from "styled-components";
import { keyframes, css } from "styled-components";

import { getCurrencySymbol, formattedNumber } from "@/utils";

const Icon = styled.div`
    display: flex;
    padding: 6px;
    border-radius: 5px;
    background-color: ${(props) => props.backgroundColor};
    margin-right: 12px;
`;

export const StickyCell = styled(TableCell)`
    position: sticky;
    left: 0;
    z-index: 1;
`;
export const CustomBox = styled(Box)`
    height: ${(props) => props.height || "auto"};
    width: ${(props) => props.width || "60%"};
    max-height: ${(props) => props.maxHeight || "80vh"};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: ${(props) => props.padding || "24px"};
    box-sizing: border-box;
    border-radius: 20px;
    overflow: ${(props) => props.overflow || "visible"};
`;
export const ShadedBox = styled.div`
    background-color: ${(props) => props.backgroundColor};
    border-radius: 10px;
    padding: 30px 20px;
    margin: 48px auto;
    box-sizing: border-box;
`;

export const StickyFooter = styled.div`
    position: fixed;
    z-index: 2;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100px;
    padding: 0px 40px;
    background-color: ${(props) => props.backgroundColor};
    box-sizing: border-box;
    border-top: ${(props) => `1px solid ${props.borderColor}`};
`;

export const FlexTable = styled.div`
    display: flex;
    flex-direction: column;
`;

export const FlexRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: ${({ padding }) => padding || "0"};
    gap: 20px;
`;

export const FlexCell = styled.div`
    font-size: ${({ fontSize }) => fontSize || "12px"};
    text-align: ${({ align }) => align || "left"};
    flex-grow: ${({ grow }) => grow || 1};
    flex-basis: ${({ minWidth }) => (minWidth ? minWidth + "px" : "60px")};
    min-width: ${({ minWidth }) => (minWidth ? minWidth + "px" : "60px")};
    padding: ${({ padding }) => padding || "0"};
`;

export const FlexTitleCell = styled(FlexCell)`
    font-size: 10px;
    padding: 0 3px;
    box-sizing: border-box;
`;

export const NumericFormatCustom = forwardRef(
    function NumericFormatCustom(props, ref) {
        const { onChange, ...other } = props;
        return (
            <NumericFormat
                {...other}
                getInputRef={ref}
                onValueChange={(values) => {
                    onChange({
                        target: {
                            name: props.name,
                            value: values.value,
                        },
                    });
                }}
                thousandSeparator
                valueIsNumericString
            />
        );
    },
);

export const SocketDiv = (props) => {
    const { children, highlights, minWidth, maxWidth } = props;
    return (
        <div
            style={{
                outline:
                    highlights.length > 0
                        ? `${highlights[0].color} 3px solid`
                        : "none",
                borderRadius: 5,
                position: "relative",
                display: "inline-block",
                width: "100%",
                marginRight: 0,
                verticalAlign: "bottom",
            }}
        >
            {children}
            {highlights.length > 0 ? (
                <p
                    style={{
                        fontSize: 10,
                        transform: "translateY(-100%)",
                        margin: 0,
                        top: 0,
                        right: 0,
                        position: "absolute",
                        background: highlights[0].color,
                        padding: 2,
                        borderRadius: 5,
                    }}
                >
                    {highlights
                        .map((highlight) => highlight.personName)
                        .join(", ")}
                </p>
            ) : null}
        </div>
    );
};

export const TableCellSmall = (props) => {
    const { children } = props;
    return (
        <TableCell
            {...props}
            sx={{ fontSize: 11, borderBottom: "none", padding: 0.5 }}
        >
            {children}
        </TableCell>
    );
};

export const TextFieldSmallAutoHighlight = (props) => {
    const { InputProps } = props;
    return (
        <TextField
            size="small"
            {...props}
            InputProps={{
                ...InputProps,
                style: { fontSize: 11, borderRadius: 8 },
            }}
            inputProps={{
                ...props.inputProps,
                style: { borderRadius: 50 },
                onClick: (e) => {
                    e.target.select();
                },
            }}
        />
    );
};

export const TextFieldNumberSmallAutoHighlight = (props) => {
    const { InputProps, customcolor } = props;
    return (
        <TextField
            size="small"
            {...props}
            InputProps={{
                ...InputProps,
                style: { fontSize: 11, color: customcolor || "auto" },
                inputComponent: NumericFormatCustom,
            }}
            inputProps={{
                ...props.inputProps,
                onClick: (e) => {
                    e.target.select();
                },
                style: { textAlign: "right" },
            }}
        />
    );
};

const Targets = styled.div`
    font-size: 14px;
    padding: 18px 12px;
    margin-bottom: 24px;

    li:not(:last-child) {
        margin-bottom: 12px;
    }
    li ul {
        margin-top: 12px;
    }
`;
export const TargetsPass = styled(Targets)`
    background-color: ${({ theme }) => theme.palette.success.dark};
    border-left: 2px solid ${({ theme }) => theme.palette.success.main};
`;
export const TargetsWarning = styled(Targets)`
    background-color: ${({ theme }) => theme.palette.warning.dark};
    border-left: 2px solid ${({ theme }) => theme.palette.warning.main};
`;
export const TargetsError = styled(Targets)`
    background-color: ${({ theme }) => theme.palette.error.dark};
    border-left: 2px solid ${({ theme }) => theme.palette.error.main};
`;
export const TargetsInfo = styled(Targets)`
    background-color: #e8e8e8;
    border-left: 1px solid rgb(0, 0, 0);
`;

export const BoxButton = (props) => {
    const { theme, icon, text, onClick } = props;
    return (
        <Box
            borderRadius={5}
            sx={{
                background: theme.palette.background.paper,
                height: "100%",
                cursor: "pointer",
            }}
            onClick={onClick}
        >
            <div style={{ padding: 30 }}>
                {icon}
                <br />
                {text}
                <br />
                <AltArrowRight size={28} />
            </div>
        </Box>
    );
};

export const SearchBox = (props) => {
    const { handleSearch } = props;
    const theme = useTheme();
    return (
        <Box
            sx={{
                borderRadius: 10,
                background: theme.palette.background.paper,
                height: "100%",
            }}
        >
            <TextField
                placeholder="Search..."
                size="small"
                onChange={handleSearch}
                defaultValue=""
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Magnifier />
                        </InputAdornment>
                    ),
                }}
                sx={{ "& fieldset": { border: "none" }, width: "100%" }}
                autoComplete="off"
            />
        </Box>
    );
};

export const FormattedCurrency = ({ number, currency, noMinWidth }) => (
    <Grid
        container
        justifyContent={"right"}
        minWidth={noMinWidth ? "none" : 100}
    >
        <span style={{ color: "grey" }}>{getCurrencySymbol(currency)}</span>
        &nbsp;
        <span>{formattedNumber(number)}</span>
    </Grid>
);

const scroll = keyframes`
  0% { background-position: 0% 0%; }
  100% { background-position: 100% 0%; }
`;
const scrollAnimation = css`
    ${scroll} 120s linear infinite
`;
const ScrollingAnimationContainer = styled.div`
    animation: ${scrollAnimation};
    animation-fill-mode: forwards;
    height: 100%;
`;
export const DottedDiv = (props) => {
    const { children, backgroundColor, dotColor, overlay, borderRadius } =
        props;
    const theme = useTheme();
    return (
        <div
            style={{
                overflow: "hidden",
                borderRadius: borderRadius || 10,
                background: backgroundColor,
                height: "100%",
            }}
        >
            <ScrollingAnimationContainer
                style={{
                    background: `radial-gradient(circle at center, ${dotColor} 40%, transparent 40%)`,
                    backgroundRepeat: "repeat",
                    backgroundSize: "20px 20px",
                    transition: "background-position 2s ease",
                }}
            >
                <div
                    style={{
                        borderRadius: borderRadius || 10,
                        height: "100%",
                        background: overlay
                            ? `linear-gradient(90deg, ${theme.palette.background.paper} 0%, #00000000 100%)`
                            : null,
                    }}
                >
                    {children}
                </div>
            </ScrollingAnimationContainer>
        </div>
    );
};

const HighlightedIconWrapper = styled.div`
    padding: 2px 3px;
    border-radius: 4px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    color: ${({ highlighted, theme }) =>
        highlighted
            ? theme.palette.primary.main
            : theme.palette.text.secondary};

    box-shadow: ${({ highlighted, theme }) =>
        highlighted
            ? `inset 0px 0px 20px ${theme.palette.secondary.main}`
            : `inset 0px 0px 0px ${theme.palette.secondary.main}`};

    transition:
        color 0.5s ease-out,
        box-shadow 0.4s 0.1s ease-in;

    svg {
        width: 14px;
        height: 14px;
    }
`;

export const HighlightedIcon = memo(({ icon, highlighted }) => {
    const Icon = icon;
    const theme = useTheme();

    return (
        <HighlightedIconWrapper theme={theme} highlighted={highlighted}>
            <Icon />
        </HighlightedIconWrapper>
    );
});

HighlightedIcon.displayName = "HighlightedIcon";

export const FileUploadContainer = styled.div`
    display: flex;
    height: 100%;
    min-height: 160px;
    max-height: ${(props) => props.maxHeight || "none"};
    align-items: ${(props) => (props.inputing ? "flex-start" : "center")};
    justify-content: center;
    padding: ${(props) => (props.inputing ? "6px" : "16px")};
    box-sizing: border-box;
    text-align: center;
    border-radius: 20px;
    overflow: auto;
    border: ${(props) =>
        `${props.borderColor || "#000"} 0.8px ${props.borderStyle || "dashed"}`};
`;

export const FileUploadFiles = styled.div`
    display: flex;
    flex-direction: column;
    background: ${(props) => props.backgroundColor || "#fff"};
    border-radius: 20px;
    margin-top: 24px;
    padding: 24px 34px;
    max-height: 160px;
    overflow-y: auto;

    h3 {
        font-weight: 600;
        font-size: 12px;
        text-align: left;
    }
`;

export const DotLoader = styled.div`
    width: 32px;
    aspect-ratio: 2;
    --_g: ${(props) =>
        `no-repeat radial-gradient(circle closest-side,${props.darkMode ? "#FFF" : "#000"} 90%,#0000)`};
    background:
        var(--_g) 0% 50%,
        var(--_g) 50% 50%,
        var(--_g) 100% 50%;
    background-size: calc(100% / 3) 50%;
    animation: l3 1s infinite linear;

    @keyframes l3 {
        20% {
            background-position:
                0% 0%,
                50% 50%,
                100% 50%;
        }
        40% {
            background-position:
                0% 100%,
                50% 0%,
                100% 50%;
        }
        60% {
            background-position:
                0% 50%,
                50% 100%,
                100% 0%;
        }
        80% {
            background-position:
                0% 50%,
                50% 50%,
                100% 100%;
        }
    }
`;
